import React, { FC } from 'react';
import { useDispatch } from 'react-redux';

import Modal from 'ui/Modal';
import { Button } from 'ui';
import { ReactComponent as CopyIcon } from 'assets/images/icons/copy.svg';
import { hideModal } from 'store/Modal/Modal.action';
import { showToast } from 'store/Toast/Toast.action';
import { LNS_UDP_HOST, LNS_UDP_PORT } from 'services/gateways';

type PropsType = {
  className?: string;
  isVisible: boolean;
  gatewayName?: string;
  eui?: string;
  freqBand?: string;
  registered?: boolean;
};

const WidgetModalGatewayConfig: FC<PropsType> = ({
  className,
  isVisible,
  gatewayName = '',
  eui = '',
  freqBand = 'EU868',
  registered = false,
}) => {
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(hideModal());
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      dispatch(showToast('Copied to clipboard', 'success'));
    } catch {
      // Clipboard API unavailable (e.g. insecure origin) — silent no-op.
    }
  };

  const networkServer = `${LNS_UDP_HOST}:${LNS_UDP_PORT}`;

  return (
    <Modal
      className={`modal-gateway-config ${className || ''}`}
      isVisible={isVisible}
      isClose
      isCloseBg
      title="Configuration instructions"
    >
      <div className="modal-gateway-config__content">
        <p className="modal-gateway-config__subtitle">
          {registered ? (
            <>
              Gateway <strong>&apos;{gatewayName}&apos;</strong> was registered.
              Configure the packet forwarder on the device to forward LoRaWAN
              frames to the network server below.
            </>
          ) : (
            <>
              Configure the packet forwarder on the device{' '}
              <strong>&apos;{gatewayName}&apos;</strong> to forward LoRaWAN
              frames to the network server below.
            </>
          )}
        </p>

        <div className="modal-gateway-config__field">
          <span className="modal-gateway-config__label">
            Network server (Semtech UDP)
          </span>
          <div className="modal-gateway-config__value">
            <span className="modal-gateway-config__value-text">
              {networkServer}
            </span>
            <button
              type="button"
              className="modal-gateway-config__copy"
              aria-label="Copy network server"
              onClick={() => handleCopy(networkServer)}
            >
              <CopyIcon />
            </button>
          </div>
          <span className="modal-gateway-config__hint">
            Enter the host part as <strong>Server Address</strong> and{' '}
            <strong>{LNS_UDP_PORT}</strong> as <strong>Port</strong> in your
            gateway&apos;s packet-forwarder settings.
          </span>
        </div>

        <div className="modal-gateway-config__field">
          <span className="modal-gateway-config__label">Gateway EUI</span>
          <div className="modal-gateway-config__value">
            <span className="modal-gateway-config__value-text">{eui}</span>
            <button
              type="button"
              className="modal-gateway-config__copy"
              aria-label="Copy Gateway EUI"
              onClick={() => handleCopy(eui)}
            >
              <CopyIcon />
            </button>
          </div>
        </div>

        <div className="modal-gateway-config__steps">
          <span className="modal-gateway-config__label">Next steps</span>
          <div className="modal-gateway-config__steps-list">
            <p>Power on the gateway and connect it to your local network.</p>
            <p>
              Open the gateway admin UI and navigate to{' '}
              <strong>&quot;Packet Forwarder&quot;</strong> →{' '}
              <strong>&quot;General&quot;</strong>.
            </p>
            <p>
              Set <strong>Mode</strong> to <strong>Semtech UDP</strong>,{' '}
              <strong>Server Address</strong> to <code>{LNS_UDP_HOST}</code>,
              and <strong>Port</strong> (uplink and downlink) to{' '}
              <strong>{LNS_UDP_PORT}</strong>.
            </p>
            <p>
              Set <strong>Frequency Band</strong> to <strong>{freqBand}</strong>.
              Save the configuration and restart the packet forwarder.
            </p>
          </div>
        </div>
      </div>

      <div className="modal-gateway-config__action">
        <Button
          size="large"
          fullWidth
          type="button"
          className="modal-gateway-config__submit"
          onClick={handleClose}
        >
          Got it
        </Button>
      </div>
    </Modal>
  );
};

export default WidgetModalGatewayConfig;
